// React and React Native Core imports
import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// 3rd party libraries
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

// Our Own imports
import { FaultCodes } from '@feedflo/fault-codes';
import Button from '../../atoms/Button';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import BinSetInventoryChart, {
  EventMarkerCloseIcon,
  EventMarkerOpenIcon,
  EventMarkerVibrationIcon,
} from '../BinSetInventoryChart/BinSetInventoryChart';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE } from '../../utils/dates';

// Relative path imports
import {
  BIN_CONTROLLER_FAULT_HISTORY_QUERY,
  BIN_SET_PER_BIN_PREDICTION_FOR_HISTORY,
} from './queries';
import {
  BIN_ACTIVITY_HISTORY_FALLBACK_DAYS,
  buildInactiveBinBackgroundBandsByBinIndex,
  buildLockdownBackgroundBands,
  buildPerBinChartRowsForActivityHistory,
  buildSlideEventMarkersByBinIndex,
  getActiveBinIndexAtEnd,
  getDomainFromPerBinCharts,
  SLIDE_EVENT_SOURCE_AUTO,
  SLIDE_EVENT_SOURCE_FEEDFLO_STAFF,
  SLIDE_EVENT_SOURCE_MANUAL,
  SLIDE_EVENT_SOURCE_MARKER_COLORS,
  SLIDE_EVENT_SOURCE_REMOTE,
} from './binControllerHistoryChartUtils';
import './BinControllerFaultHistoryModal.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
const CHART_MIN_HEIGHT = 120;
const CHART_MARGIN = { top: 0, bottom: 28, left: 50, right: 20 };

const DEFAULT_OVERLAY_VISIBILITY = {
  softLockdown: true,
  hardLockdown: true,
  inactiveBin: true,
  openMarkers: true,
  closeMarkers: false,
  vibrationMarkers: false,
  sourceManual: true,
  sourceRemote: true,
  sourceAuto: true,
  sourceStaff: true,
};

const SOURCE_MANUAL_GROUP_TOGGLE_KEY = 'sourceManualGroup';

const SOURCE_KEY_FOR_OVERLAY_VISIBILITY = {
  [SLIDE_EVENT_SOURCE_MANUAL]: 'sourceManual',
  [SLIDE_EVENT_SOURCE_REMOTE]: 'sourceRemote',
  [SLIDE_EVENT_SOURCE_AUTO]: 'sourceAuto',
  [SLIDE_EVENT_SOURCE_FEEDFLO_STAFF]: 'sourceStaff',
};

/**
 * @param {Array<{ label?: string, tStart: number, tEnd: number, color: string, opacity?: number }>} lockdownBands
 * @returns {{ soft: typeof lockdownBands, hard: typeof lockdownBands }}
 */
function splitLockdownBandsByKind(lockdownBands) {
  const soft = [];
  const hard = [];
  for (const b of lockdownBands) {
    if (b.label === 'Soft lockdown') soft.push(b);
    else if (b.label === 'Hard lockdown') hard.push(b);
  }
  return { soft, hard };
}

/**
 * @param {Array<{ kind: string, source?: string }>} markers
 * @param {Record<string, boolean>} v overlay visibility flags
 */
function filterMarkersByOverlayVisibility(markers, v) {
  return markers.filter((m) => {
    if (m.kind === 'open' && !v.openMarkers) return false;
    if (m.kind === 'close' && !v.closeMarkers) return false;
    if (m.kind === 'vibration' && !v.vibrationMarkers) return false;
    const sk = SOURCE_KEY_FOR_OVERLAY_VISIBILITY[m.source];
    if (sk == null) {
      return Boolean(v.sourceManual && v.sourceRemote && v.sourceStaff);
    }
    return Boolean(v[sk]);
  });
}

/**
 * Single clickable legend chip. The whole chip is the toggle (industry standard); when hidden the
 * chip dims and the label strikes through.
 */
function LegendChip({
  visibilityKey,
  visible,
  onToggleKey,
  label,
  tooltipDescription,
  glyph,
}) {
  return (
    <FeedFloToolTip description={tooltipDescription} position="top">
      <button
        type="button"
        className="BinControllerFaultHistoryModal-legendChip"
        aria-pressed={visible}
        aria-label={visible ? `Hide ${label} on charts` : `Show ${label} on charts`}
        onClick={() => onToggleKey(visibilityKey)}
      >
        <span className="BinControllerFaultHistoryModal-legendChipGlyph" aria-hidden>
          {glyph}
        </span>
        <span className="BinControllerFaultHistoryModal-legendChipLabel">{label}</span>
      </button>
    </FeedFloToolTip>
  );
}

LegendChip.propTypes = {
  visibilityKey: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onToggleKey: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  tooltipDescription: PropTypes.string.isRequired,
  glyph: PropTypes.node.isRequired,
};

const BIN_CONTROLLER_FAULT_CODE_LIST = [
  FaultCodes.BIN_CONTROLLER_SLIDE_OPEN,
  FaultCodes.BIN_CONTROLLER_MANUAL_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_AUTOMATIC_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_INTERNET_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_UNKNOWN_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_MANUAL_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_AUTOMATIC_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_INTERNET_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_UNKNOWN_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN,
  FaultCodes.BIN_CONTROLLER_SOFT_LOCK_DOWN,
];

function BinControllerFaultHistoryModalView({
  dialogRef = null,
  onClose = () => { },
  deviceId = null,
  binSetId = null,
  isMetric = false,
  bins = [],
  binControllerBins = [],
}) {
  const [sharedZoomDomain, setSharedZoomDomain] = useState(null);
  const [overlayVisibility, setOverlayVisibility] = useState(() => ({ ...DEFAULT_OVERLAY_VISIBILITY }));

  // --- Apollo/GraphQL hooks ---
  const {
    data: predictionData,
    loading: predictionLoading,
    error: predictionError,
  } = useQuery(BIN_SET_PER_BIN_PREDICTION_FOR_HISTORY, {
    variables: { binSetId },
    skip: !binSetId,
    context: { headers: { 'x-hasura-role': 'user' } },
    fetchPolicy: 'network-only',
  });

  const predictionRow = predictionData?.bin_set_by_pk?.bin_set_level_predictions?.[0] ?? null;

  const perBinChartData = useMemo(() => {
    if (!predictionRow) return null;
    return {
      pastByBinId: predictionRow.per_bin_past_feed_inventory_time_series || null,
      deliveredByBinId: predictionRow.per_bin_delivered_deliveries_projection_time_series || null,
      scheduledByBinId: predictionRow.per_bin_scheduled_deliveries_projection_time_series || null,
      recommendedByBinId: predictionRow.per_bin_recommended_deliveries_projection_time_series || null,
    };
  }, [predictionRow]);

  const perBinChartRows = useMemo(
    () => buildPerBinChartRowsForActivityHistory(bins, binControllerBins, perBinChartData),
    [bins, binControllerBins, perBinChartData],
  );

  const domainFromSeries = useMemo(() => getDomainFromPerBinCharts(perBinChartRows), [perBinChartRows]);

  const { domainStart, domainEnd } = useMemo(() => {
    const nowUnix = dayjs.tz().unix();
    const fallbackStart = dayjs.tz().subtract(BIN_ACTIVITY_HISTORY_FALLBACK_DAYS, 'day').unix();
    if (domainFromSeries) {
      // Per-bin domain includes projection series (future); charts only show past and no future axis.
      const endClamped = Math.min(domainFromSeries.domainEnd, nowUnix);
      return {
        domainStart: domainFromSeries.domainStart,
        domainEnd: Math.max(domainFromSeries.domainStart, endClamped),
      };
    }
    return { domainStart: fallbackStart, domainEnd: nowUnix };
  }, [domainFromSeries]);

  // --- Navigation/side effects ---
  useEffect(() => {
    setSharedZoomDomain(null);
    setOverlayVisibility({ ...DEFAULT_OVERLAY_VISIBILITY });
  }, [domainStart, domainEnd]);

  const faultQuerySkip = !deviceId;

  const {
    data: faultData,
    error: faultError,
  } = useQuery(BIN_CONTROLLER_FAULT_HISTORY_QUERY, {
    variables: {
      deviceId,
      faultCodes: BIN_CONTROLLER_FAULT_CODE_LIST,
      startTimestamp: domainStart,
      endTimestamp: domainEnd,
    },
    skip: faultQuerySkip,
    context: { headers: { 'x-hasura-role': 'user' } },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (predictionError) {
      console.error('Bin controller history prediction query failed', predictionError);
      Sentry.captureException(predictionError);
    }
  }, [predictionError]);

  useEffect(() => {
    if (faultError) {
      console.error('Bin controller history fault query failed', faultError);
      Sentry.captureException(faultError);
    }
  }, [faultError]);

  // --- Derived state ---
  const faults = useMemo(() => {
    const inPeriod = faultData?.fault ?? [];
    const beforePeriod = faultData?.fault_before_period?.[0];
    if (!beforePeriod) return inPeriod;
    if (inPeriod.some((f) => f.id === beforePeriod.id)) return inPeriod;
    return [...inPeriod, beforePeriod];
  }, [faultData]);

  const faultsAsc = useMemo(
    () => [...faults].sort((a, b) => Number(a.started_at) - Number(b.started_at)),
    [faults],
  );

  const nowUnix = Math.floor(Date.now() / 1000);

  const lockdownBands = useMemo(
    () => buildLockdownBackgroundBands(faults, domainStart, domainEnd, nowUnix),
    [faults, domainStart, domainEnd, nowUnix],
  );

  const { soft: softLockdownBandsForChart, hard: hardLockdownBandsForChart } = useMemo(
    () => splitLockdownBandsByKind(lockdownBands),
    [lockdownBands],
  );

  /** Lockdown intervals carved out of inactive grey only for lockdown types that are visible in the legend. */
  const lockdownIntervalsForInactiveCompute = useMemo(() => {
    const parts = [];
    if (overlayVisibility.softLockdown) parts.push(...softLockdownBandsForChart);
    if (overlayVisibility.hardLockdown) parts.push(...hardLockdownBandsForChart);
    return parts;
  }, [
    overlayVisibility.softLockdown,
    overlayVisibility.hardLockdown,
    softLockdownBandsForChart,
    hardLockdownBandsForChart,
  ]);

  const inactiveBinBandsByBinIndex = useMemo(
    () =>
      buildInactiveBinBackgroundBandsByBinIndex(
        faultsAsc,
        domainStart,
        domainEnd,
        lockdownIntervalsForInactiveCompute,
        perBinChartRows.map((r) => r.binIndex),
      ),
    [faultsAsc, domainStart, domainEnd, lockdownIntervalsForInactiveCompute, perBinChartRows],
  );

  const markersByBinIndex = useMemo(() => buildSlideEventMarkersByBinIndex(faultsAsc), [faultsAsc]);

  const activeBinIndexAtEnd = useMemo(() => getActiveBinIndexAtEnd(faultsAsc, domainEnd), [faultsAsc, domainEnd]);

  const predictedAtLabel = useMemo(() => {
    const raw = predictionRow?.predicted_at;
    if (raw == null) return null;
    const sec = typeof raw === 'string' ? Number.parseInt(raw, 10) : Number(raw);
    if (!Number.isFinite(sec)) return null;
    const ms = sec > 1e12 ? sec : sec * 1000;
    return dayjs.tz(ms).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE);
  }, [predictionRow]);

  // --- Function definitions ---
  const onCloseDialog = () => {
    if (!dialogRef?.current) {
      return;
    }
    dialogRef.current.close();
    onClose();
  };

  const handleBinHistoryZoomDomainChange = useCallback((next) => {
    setSharedZoomDomain(next);
  }, []);

  const handleOverlayVisibilityToggle = useCallback((key) => {
    setOverlayVisibility((prev) => {
      if (key === SOURCE_MANUAL_GROUP_TOGGLE_KEY) {
        const manualGroupOn = prev.sourceManual && prev.sourceRemote && prev.sourceStaff;
        const nextOn = !manualGroupOn;
        return { ...prev, sourceManual: nextOn, sourceRemote: nextOn, sourceStaff: nextOn };
      }
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  // --- Render ---
  return (
    <dialog ref={dialogRef} className="BinControllerFaultHistoryModal" onClose={onCloseDialog}>
      <div className="BinControllerFaultHistoryModal-content">
        <div className="BinControllerFaultHistoryModal-header">
          <h2>Bin Controller Activity History</h2>
          <button className="BinControllerFaultHistoryModal-closeButton" onClick={onCloseDialog} type="button">
            ×
          </button>
        </div>

        <div className="BinControllerFaultHistoryModal-body">
          <div className="BinControllerFaultHistoryModal-bodyMeta">
            {predictedAtLabel &&
              !(binSetId && !predictionError && perBinChartRows.length > 0) ? (
              <p className="BinControllerFaultHistoryModal-asOf">Inventory as of {predictedAtLabel}</p>
            ) : null}

            {!binSetId ? (
              <p className="BinControllerFaultHistoryModal-hint">
                Per-bin inventory charts need a bin set id. Without chart data, the time range defaults to the last{' '}
                {BIN_ACTIVITY_HISTORY_FALLBACK_DAYS} days.
              </p>
            ) : null}

            {binSetId && predictionLoading && !predictionError && (
              <p className="BinControllerFaultHistoryModal-hint">Loading latest inventory prediction…</p>
            )}

            {binSetId && predictionError && (
              <div className="BinControllerFaultHistoryModal-error">
                <p>Error loading inventory prediction: {predictionError.message}</p>
              </div>
            )}

            {binSetId && !predictionError && perBinChartRows.length === 0 && (
              <p className="BinControllerFaultHistoryModal-hint">No per-bin chart data for this bin set.</p>
            )}

            {faultError && (
              <div className="BinControllerFaultHistoryModal-error">
                <p>Error loading fault history: {faultError.message}</p>
              </div>
            )}
          </div>

          {binSetId && !predictionError && perBinChartRows.length > 0 && (
            <div className="BinControllerFaultHistoryModal-charts">
              <div className="BinControllerFaultHistoryModal-legendHeader">
                <div className="BinControllerFaultHistoryModal-legend">
                  <div className="BinControllerFaultHistoryModal-legendGroup">
                    <span className="BinControllerFaultHistoryModal-legendGroupLabel">Bands</span>
                    <LegendChip
                      visibilityKey="softLockdown"
                      visible={overlayVisibility.softLockdown}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Soft lockdown"
                      tooltipDescription="Show or hide soft lockdown shading on all bin charts."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendSwatch BinControllerFaultHistoryModal-legendSwatch--soft" />
                      }
                    />
                    <LegendChip
                      visibilityKey="hardLockdown"
                      visible={overlayVisibility.hardLockdown}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Hard lockdown"
                      tooltipDescription="Show or hide hard lockdown shading on all bin charts."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendSwatch BinControllerFaultHistoryModal-legendSwatch--hard" />
                      }
                    />
                    <LegendChip
                      visibilityKey="inactiveBin"
                      visible={overlayVisibility.inactiveBin}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Another bin selected"
                      tooltipDescription="Show or hide grey shading for times another bin was selected."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendSwatch BinControllerFaultHistoryModal-legendSwatch--inactiveBin" />
                      }
                    />
                  </div>

                  <div className="BinControllerFaultHistoryModal-legendGroup">
                    <span className="BinControllerFaultHistoryModal-legendGroupLabel">Action</span>
                    <LegendChip
                      visibilityKey="openMarkers"
                      visible={overlayVisibility.openMarkers}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Open"
                      tooltipDescription="Show or hide Open action markers on the bin that was selected."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendEventMarker BinControllerFaultHistoryModal-legendEventMarker--interactionNeutral">
                          <EventMarkerOpenIcon />
                        </span>
                      }
                    />
                    <LegendChip
                      visibilityKey="closeMarkers"
                      visible={overlayVisibility.closeMarkers}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Close"
                      tooltipDescription="Show or hide implicit Close markers on the previously selected bin (a different bin being opened closes the prior one)."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendEventMarker BinControllerFaultHistoryModal-legendEventMarker--interactionNeutral">
                          <EventMarkerCloseIcon />
                        </span>
                      }
                    />
                    <LegendChip
                      visibilityKey="vibrationMarkers"
                      visible={overlayVisibility.vibrationMarkers}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Vibration"
                      tooltipDescription="Show or hide Vibration action markers on all bin charts."
                      glyph={
                        <span className="BinControllerFaultHistoryModal-legendEventMarker BinControllerFaultHistoryModal-legendEventMarker--interactionNeutral">
                          <EventMarkerVibrationIcon />
                        </span>
                      }
                    />
                  </div>

                  <div className="BinControllerFaultHistoryModal-legendGroup">
                    <span className="BinControllerFaultHistoryModal-legendGroupLabel">Source</span>
                    <LegendChip
                      visibilityKey={SOURCE_MANUAL_GROUP_TOGGLE_KEY}
                      visible={
                        overlayVisibility.sourceManual &&
                        overlayVisibility.sourceRemote &&
                        overlayVisibility.sourceStaff
                      }
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Manual"
                      tooltipDescription="Manual operator sources (on-site switch, local/network, remote, FeedFlo staff) share this violet marker color; hover a marker to see the exact source."
                      glyph={
                        <span
                          className="BinControllerFaultHistoryModal-legendSwatch BinControllerFaultHistoryModal-legendSwatch--source"
                          style={{ backgroundColor: SLIDE_EVENT_SOURCE_MARKER_COLORS[SLIDE_EVENT_SOURCE_MANUAL] }}
                        />
                      }
                    />
                    <LegendChip
                      visibilityKey="sourceAuto"
                      visible={overlayVisibility.sourceAuto}
                      onToggleKey={handleOverlayVisibilityToggle}
                      label="Auto"
                      tooltipDescription="Show or hide slide markers whose source is Auto."
                      glyph={
                        <span
                          className="BinControllerFaultHistoryModal-legendSwatch BinControllerFaultHistoryModal-legendSwatch--source"
                          style={{ backgroundColor: SLIDE_EVENT_SOURCE_MARKER_COLORS[SLIDE_EVENT_SOURCE_AUTO] }}
                        />
                      }
                    />
                  </div>
                </div>
                {predictedAtLabel ? (
                  <p className="BinControllerFaultHistoryModal-asOf">Inventory as of {predictedAtLabel}</p>
                ) : null}
              </div>
              {perBinChartRows.map((row) => {
                const eventMarkers = markersByBinIndex.get(row.binIndex) || [];
                const inactiveBandsForBin = inactiveBinBandsByBinIndex.get(row.binIndex) || [];
                const chartBandParts = [];
                if (overlayVisibility.inactiveBin) chartBandParts.push(...inactiveBandsForBin);
                if (overlayVisibility.softLockdown) chartBandParts.push(...softLockdownBandsForChart);
                if (overlayVisibility.hardLockdown) chartBandParts.push(...hardLockdownBandsForChart);
                const chartBands = chartBandParts;
                const markersForChart = filterMarkersByOverlayVisibility(eventMarkers, overlayVisibility);
                return (
                  <div key={row.binId} className="BinControllerFaultHistoryModal-chartBlock">
                    <BinSetInventoryChart
                      chartHeading={row.binName}
                      step_bin_level_history={row.step_bin_level_history}
                      projection_past_deliveries={row.projection_past_deliveries}
                      projection_with_scheduled={row.projection_with_scheduled}
                      projection_with_recommended={row.projection_with_recommended}
                      showProjectionLines={false}
                      showLineLegend={false}
                      fillParentHeight
                      chartStartTime={domainStart}
                      chartEndTime={domainEnd}
                      chartHeight={CHART_MIN_HEIGHT}
                      chartMargin={CHART_MARGIN}
                      isMetric={isMetric}
                      totalBinCapacity={row.capacityGrams}
                      chartBackgroundBands={chartBands}
                      eventMarkers={markersForChart}
                      sharedZoomDomain={sharedZoomDomain}
                      onZoomDomainChange={handleBinHistoryZoomDomainChange}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="BinControllerFaultHistoryModal-footer">
          <Button
            type="button"
            onClick={onCloseDialog}
            content="Close"
            variant="vivid"
            color="secondary"
            className="BinControllerFaultHistoryModal-closeButtonFooter"
          />
        </div>
      </div>
    </dialog>
  );
}

BinControllerFaultHistoryModalView.propTypes = {
  dialogRef: PropTypes.object,
  onClose: PropTypes.func,
  deviceId: PropTypes.string,
  binSetId: PropTypes.string,
  isMetric: PropTypes.bool,
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      capacityInGrams: PropTypes.number,
    }),
  ),
  binControllerBins: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      bin: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  ),
};

function BinControllerFaultHistoryModal({
  open = false,
  onClose = () => { },
  deviceId = null,
  binSetId = null,
  isMetric = false,
  bins = [],
  binControllerBins = [],
}) {
  // --- State hooks ---
  const dialogRef = useRef(null);

  // --- Navigation/side effects ---
  useEffect(() => {
    if (dialogRef?.current) {
      if (open) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }
  }, [open]);

  return ReactDOM.createPortal(
    <BinControllerFaultHistoryModalView
      dialogRef={dialogRef}
      onClose={onClose}
      deviceId={deviceId}
      binSetId={binSetId}
      isMetric={isMetric}
      bins={bins}
      binControllerBins={binControllerBins}
    />,
    document.getElementById('portal-root'),
  );
}

BinControllerFaultHistoryModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  deviceId: PropTypes.string,
  binSetId: PropTypes.string,
  isMetric: PropTypes.bool,
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      capacityInGrams: PropTypes.number,
    }),
  ),
  binControllerBins: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      bin: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  ),
};

export default BinControllerFaultHistoryModal;
